import React, { useEffect, useState, useRef } from "react"
import {
  useSearchspring,
  useSearchspringSearchContext,
} from "@app/scripts/providers/searchspring"
import {
  SearchspringSuggest,
  type SearchspringSuggestResponse,
} from "@app/scripts/lib/searchspring"
import { SearchDrawerMenu } from "@app/scripts/components/search-drawer-menu"
import { SearchDrawerResults } from "@app/scripts/components/search-drawer-results"
import {
  SearchDrawerSuggested,
  type SuggestedTerm,
} from "@app/scripts/components/search-drawer-suggested"
import { SearchNoResults } from "@app/scripts/components/search-no-results"
import { excludeProducts } from "@app/scripts/lib/excluded-products"
import { NoResultsItem } from "@app/types"

export type Props = {
  noResultsData?: NoResultsItem[]
  suggestedTerms?: SuggestedTerm[]
}

export function InstantSearch({ suggestedTerms }: Props) {
  const [searchTerm, setSearchTerm] = useState("")
  // Debounced search term added for the suggestions query.
  // This stops it querying the first change which would be the first typed letter or us making multiple requests.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")

  const { response, refine, isLoading } = useSearchspringSearchContext()

  // The autocomplete endpoint spell-corrects raw fragments to the wrong word
  // (e.g. "Hemin" -> "herrin", surfacing an unrelated product). So we run a
  // dedicated Suggest query and drive the product cards off its completion -
  // and reuse the same response to render the text suggestions below.
  const suggestClient = useRef(new SearchspringSuggest("products"))
  const {
    response: suggestResponse,
    refine: refineSuggest,
    error: suggestError,
    isLoading: suggestLoading,
  } = useSearchspring<SearchspringSuggest, SearchspringSuggestResponse>({
    client: suggestClient.current,
    isInitiallyRun: false,
    shouldSkipEmptyOptions: true,
  })

  const results = excludeProducts(response?.results || [])

  const responseRef = useRef(response)

  useEffect(() => {
    responseRef.current = response
  }, [response])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm === debouncedSearchTerm) return
      setDebouncedSearchTerm(searchTerm)
    }, 700)

    return () => clearTimeout(handler)
  }, [searchTerm])

  // Fetch suggestions for the debounced term.
  useEffect(() => {
    if (debouncedSearchTerm === "") return
    refineSuggest("query", debouncedSearchTerm)
  }, [debouncedSearchTerm])

  // Query the product cards with the Suggest completion (falling back to the
  // raw term only if Suggest errors). Waiting for Suggest to resolve first
  // means we never query products with the raw fragment.
  useEffect(() => {
    if (debouncedSearchTerm === "") return
    if (suggestResponse === undefined && !suggestError) return
    const completion = suggestResponse?.suggested?.text
    refine("query", completion || debouncedSearchTerm)
  }, [suggestResponse, suggestError, debouncedSearchTerm])

  useEffect(() => {
    const clearBtn = document.querySelector(".search-clear--btn")
    if (clearBtn) {
      clearBtn.classList.toggle("is-hidden", searchTerm === "")
    }
  }, [searchTerm])

  // Handles shopify liquid drawers input and input clear button
  useEffect(() => {
    const searchInput = document.getElementById(
      "side-panel-search-input"
    ) as HTMLInputElement | null
    const clearBtn = document.querySelector(
      ".search-clear--btn"
    ) as HTMLButtonElement | null

    if (!searchInput) return

    const handleInput = (e: Event) =>
      setSearchTerm((e.target as HTMLInputElement).value ?? "")
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        const query = searchInput.value.trim()
        if (!query) return

        // Check if a merchandising redirect is already available
        if (responseRef.current?.merchandising?.redirect) {
          window.location.href = responseRef.current.merchandising.redirect
          return
        }

        window.location.href = `/search?query=${encodeURIComponent(query)}`
      }
    }
    const handleClear = () => {
      searchInput.value = ""
      setSearchTerm("")
    }

    searchInput.addEventListener("input", handleInput)
    searchInput.addEventListener("keydown", handleKeyDown)
    clearBtn?.addEventListener("click", handleClear)

    return () => {
      searchInput.removeEventListener("input", handleInput)
      searchInput.removeEventListener("keydown", handleKeyDown)
      clearBtn?.removeEventListener("click", handleClear)
    }
  }, [])

  const searchDrawerTitle =
    results.length > 0 && searchTerm.length > 0 ? "Suggestions" : "Suggested"

  const noResults =
    results.length === 0 && searchTerm !== ""
      ? `No results for "${searchTerm}". Check your spelling, try another search or browse our popular collections below.`
      : ""

  const showNoResults =
    debouncedSearchTerm !== "" &&
    !suggestLoading &&
    !isLoading &&
    !results?.length

  // Below items to be reviewed and implemented when search spring data is available - awaiting searchspring response
  return (
    <div className="ss-search-drawer">
      {/* Search drawer menu */}
      <div
        data-ui={!!debouncedSearchTerm && "hidden"}
        className="ss-search-drawer__results"
      >
        <SearchDrawerSuggested terms={suggestedTerms} />
      </div>
      <div
        data-ui={!debouncedSearchTerm && "hidden"}
        className="ss-search-drawer__results"
      >
        <SearchDrawerMenu
          showNoResults={showNoResults}
          title={searchDrawerTitle}
          noResults={noResults}
          response={suggestResponse}
        />
      </div>
      <div
        data-ui={!debouncedSearchTerm && "hidden"}
        className="ss-search-drawer__results"
      >
        {!results.length && <SearchNoResults />}
      </div>
      {!!debouncedSearchTerm && <SearchDrawerResults resultsData={results} />}

      {/* Search drawer view results btn - in this file */}
      {results.length > 0 && searchTerm.length > 0 && (
        <div className="search-drawer--footer">
          <a
            href={
              response?.merchandising?.redirect
                ? response?.merchandising?.redirect
                : `/search?query=${searchTerm}`
            }
            className="search-drawer--footer-results-btn button large"
          >
            View {response?.pagination?.totalResults} Results
          </a>
        </div>
      )}
    </div>
  )
}
